import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  verifyPayment,
  verifyPaystackPayment,
  verifyFlutterwavePayment,
  selectVerifyingPayment,
  selectVerificationResult,
  selectVerificationError,
  clearCurrentPayment,
} from "@/store/slices/paymentSlice";
import { clearCurrentOrder } from "@/store/slices/orderSlice";
import { useAuth } from "@/context/Auth";
import { selectUser } from "@/store/slices/authSlice";
import store from "@/store";
import { CheckCircle, XCircle, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Seo from "@/seo/seo";
import { routeSeoConfig } from "@/utils/seo-config";
import axios from "@/utils/axios";

export default function PaymentCallback() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, setUser, fetchUser } = useAuth();
  const reduxUser = useAppSelector(selectUser);
  const hasVerifiedRef = useRef(false);

  const verifying = useAppSelector(selectVerifyingPayment);
  const verificationResult = useAppSelector(selectVerificationResult);
  const verificationError = useAppSelector(selectVerificationError);

  const [verificationStatus, setVerificationStatus] = useState("pending");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    // Prevent multiple verifications
    if (hasVerifiedRef.current) {
      return;
    }

    // Read directly from window.location.search
    const urlParams = new URLSearchParams(window.location.search);
    const reference = urlParams.get("reference") || urlParams.get("trxref");
    const transactionId = urlParams.get("transaction_id") || urlParams.get("tx_ref");
    
    const gateway = urlParams.get("gateway") || 
                   (reference ? "paystack" : transactionId ? "flutterwave" : null);

    // Mark as verified to prevent re-running
    hasVerifiedRef.current = true;

    // Clear any previous payment state
    dispatch(clearCurrentPayment());

    if (!reference && !transactionId) {
      // No reference found - show toast and redirect to cart
      toast.error("Invalid payment callback. No reference found.");
      navigate("/cart", { replace: true });
      return;
    }

    // Verify payment
    const verify = async () => {
      try {
        let result;
        
        if (gateway === "paystack" || reference) {
          result = await dispatch(
            verifyPaystackPayment(reference || transactionId || "")
          ).unwrap();
        } else if (gateway === "flutterwave" || transactionId) {
          result = await dispatch(
            verifyFlutterwavePayment(transactionId || reference || "")
          ).unwrap();
        } else {
          // Try unified verification
          result = await dispatch(
            verifyPayment({
              reference: reference || undefined,
              transactionId: transactionId || undefined,
              gateway: gateway || undefined,
            })
          ).unwrap();
        }

        if (result && (result.status === "success" || result.status === "successful")) {
          setVerificationStatus("success");
          toast.success("Payment verified successfully!");
          
          // Ensure user is authenticated before redirecting to dashboard
          const ensureAuth = async () => {
            try {
              // Always check authentication to ensure user state is set
              // Use fetchUser from Auth context which properly updates Redux state
              await fetchUser();
              
              // Wait for user state to be updated in Redux (poll for up to 2 seconds)
              let attempts = 0;
              const maxAttempts = 20;
              while (attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 100));
                // Get current user from Redux store directly
                const state = store.getState();
                const currentUser = state.auth?.user;
                if (currentUser && (currentUser.email || currentUser.id || currentUser._id)) {
                  // User is authenticated, safe to redirect
                  const orderId = result.orderId || result.metadata?.orderId;
                  if (orderId) {
                    // Clear any cached order data before navigating to ensure fresh data is fetched
                    dispatch(clearCurrentOrder());
                    navigate(`/dashboard/orders/${orderId}`, { replace: true });
                  } else {
                    navigate("/dashboard/orders", { replace: true });
                  }
                  return;
                }
                attempts++;
              }
              
              // If we get here, user wasn't set after max attempts
              // Still try to redirect - PrivateRoute will handle redirecting to login if needed
              const orderId = result.orderId || result.metadata?.orderId;
              if (orderId) {
                // Clear any cached order data before navigating to ensure fresh data is fetched
                dispatch(clearCurrentOrder());
                navigate(`/dashboard/orders/${orderId}`, { replace: true });
              } else {
                navigate("/dashboard/orders", { replace: true });
              }
            } catch (error) {
              console.error("Auth check error:", error);
              // If auth check fails, still try to redirect
              // PrivateRoute will handle redirecting to login if user is not authenticated
              const orderId = result.orderId || result.metadata?.orderId;
              if (orderId) {
                // Clear any cached order data before navigating to ensure fresh data is fetched
                dispatch(clearCurrentOrder());
                navigate(`/dashboard/orders/${orderId}`, { replace: true });
              } else {
                navigate("/dashboard/orders", { replace: true });
              }
            }
          };
          
          // Wait 1.5 seconds before checking auth and redirecting
          setTimeout(() => {
            ensureAuth();
          }, 1500);
        } else {
          setVerificationStatus("failed");
          setErrorMessage(
            result?.message || "Payment verification failed. Please contact support."
          );
          toast.error("Payment verification failed");
        }
      } catch (error) {
        setVerificationStatus("failed");
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to verify payment. Please contact support.";
        setErrorMessage(message);
        toast.error(message);
      }
    };

    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  const handleRetry = () => {
    navigate("/checkout", { replace: true });
  };

  const handleViewOrders = () => {
    navigate("/dashboard/orders", { replace: true });
  };

  return (
    <>
      <Seo
        {...routeSeoConfig.paymentCallback}
        type="website"
        noindex={routeSeoConfig.paymentCallback.noindex}
      />
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          {verificationStatus === "pending" && (
            <>
              <div className="flex justify-center mb-6">
                <Loader2 className="w-16 h-16 text-primary animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-primary mb-2">
                Verifying Payment
              </h1>
              <p className="text-gray-600">
                Please wait while we verify your payment...
              </p>
            </>
          )}

          {verificationStatus === "success" && (
            <>
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-primary mb-2">
                Payment Successful!
              </h1>
              <p className="text-gray-600 mb-6">
                Your payment has been verified successfully. Redirecting to your order...
              </p>
              {verificationResult && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                  <div className="space-y-2 text-sm">
                    {verificationResult.reference && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Reference:</span>
                        <span className="font-medium">{verificationResult.reference}</span>
                      </div>
                    )}
                    {verificationResult.amount && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Amount:</span>
                        <span className="font-medium">
                          ₦{new Intl.NumberFormat("en-NG").format(
                            verificationResult.amount / 100
                          )}
                        </span>
                      </div>
                    )}
                    {verificationResult.gateway && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Gateway:</span>
                        <span className="font-medium capitalize">
                          {verificationResult.gateway}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <Button
                onClick={handleViewOrders}
                className="w-full bg-primary hover:bg-primary/90"
              >
                View My Orders
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}

          {verificationStatus === "failed" && (
            <>
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-primary mb-2">
                Payment Verification Failed
              </h1>
              <p className="text-gray-600 mb-6">{errorMessage}</p>
              <div className="space-y-3">
                <Button
                  onClick={handleRetry}
                  variant="outline"
                  className="w-full"
                >
                  Try Again
                </Button>
                <Button
                  onClick={handleViewOrders}
                  className="w-full bg-primary hover:bg-primary/90"
                >
                  View My Orders
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

